import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { type Brand } from "types"
import { tintVTOService } from "services/tintvto.ts"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useAlert } from "@/providers/AlertProvider"
import { getGenericUserErrorMessage } from "@/utils/userFacingErrors"

export const BRANDS_QUERY_KEY = ["brands"]
export const useBrandsQuery = () => {
  const query = useQuery<Brand[]>({
    queryKey: BRANDS_QUERY_KEY,
    queryFn: async () => {
      return tintVTOService.brands()
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  })
  return {
    ...query,
    data: query.data || [],
  }
}

export const useSaveBrandMutation = () => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { showAlert } = useAlert()

  return useMutation({
    mutationFn: async (brand: Brand) => {
      const response = await tintVTOService.saveBrand(brand)
      if (response.errors) throw new Error(getGenericUserErrorMessage(t))

      return response
    },
    onSuccess: (_, brand) => {
      showAlert({
        error: false,
        text: t("brandPage.saveSuccess", {
          name: brand.name,
          action: brand.id ? t("brandPage.updated") : t("brandPage.created"),
        }),
      })
      queryClient.resetQueries({ queryKey: BRANDS_QUERY_KEY })
      navigate(`/brands`)
    },
    onError: () => {
      showAlert({
        error: true,
        text: getGenericUserErrorMessage(t),
      })
    },
  })
}
